import { Observable, throwError, timer } from 'rxjs';
import { mergeMap, retryWhen } from 'rxjs/operators';

export interface RetryBackoffOptions {
  initialDuration?: number;
  maxRetries?: number;
  maxDuration?: number;
  canRetry?: (error: unknown) => boolean;
  backoffDelay?: (iteration: number, initialDuration: number) => number;
}

/**
 * Retry on error while increasing delay between attempts.
 * @param options sets the initialDuration (default: 1000), maxRetries (default: 3), maxDuration,
 * canRetry (default: true), backoffDelay (default: iteration * initialDuration)
 * @returns Observable&lt;T&gt;
 */
export function retryBackoff<T>({
  initialDuration = 1000,
  maxRetries = 3,
  maxDuration,
  canRetry = (error) => true,
  backoffDelay = (iteration, duration) => iteration * duration,
}: RetryBackoffOptions = {}) {
  return (error$: Observable<T>) => {
    return error$.pipe(
      retryWhen(
        mergeMap((error, index) => {
          const retryCount = index + 1;
          let delay = backoffDelay(retryCount, initialDuration);
          delay = maxDuration == null || delay < maxDuration ? delay : maxDuration;
          const maxAttempts = maxRetries == null || maxRetries < 0 ? retryCount : maxRetries;

          return retryCount > maxAttempts || canRetry(error) === false
            ? throwError(error)
            : timer(delay);
        }),
      ),
    );
  };
}
